import { useState, useEffect } from 'react';
import { Modal, Button, Table, Tabs, Form, SelectPicker, TagGroup, Tag, Input } from 'rsuite';
import { formatDateTime } from '../utils/dateUtils';

const { Column, HeaderCell, Cell } = Table;

const buildInstanceTypeOptions = (droplets, currentType) => {
  const options = (droplets || [])
    .filter(d => d.available)
    .map(d => ({
      label: `${d.slug} (${d.description || ''})`,
      value: d.slug,
    }));
  const current = (currentType || '').trim();
  if (current && !options.some(o => o.value === current)) {
    options.unshift({ label: `${current} (currently set, unavailable)`, value: current });
  }
  return options;
};

function InferenceDetailsDialog({
  show, 
  onClose, 
  modelId, 
  modelDetails, 
  detailsLoading,
  editingDetails,
  editFormValue,
  savingDetails,
  droplets,
  savedModels,
  onEditChange,
  onSave,
  onCancelEdit,
  onStartEdit
}) {
  const [activeTab, setActiveTab] = useState('table');
  const [localEditing, setLocalEditing] = useState(false);
  const isEditing = editingDetails || localEditing;
  const updateEditField = (field, value) => {
    if (onEditChange) {
      onEditChange({
        ...editFormValue,
        [field]: value,
      });
    }
  };

  // Reset to table tab when dialog opens
  useEffect(() => {
    if (show) {
      setActiveTab('table');
      setLocalEditing(false);
    }
  }, [show]);

  return (
    <Modal 
      open={show} 
      onClose={() => {
        setLocalEditing(false);
        onClose();
        if (onCancelEdit) {
          onCancelEdit();
        }
      }}
      size="lg"
    >
      <Modal.Header>
        <Modal.Title>Model Details: {modelId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {detailsLoading ? (
          <div>Loading details...</div>
        ) : modelDetails?.error ? (
          <div style={{ color: 'red' }}>Error: {modelDetails.error}</div>
        ) : modelDetails ? (
          <Tabs activeKey={activeTab} onSelect={setActiveTab}>
            <Tabs.Tab eventKey="table" title="Table">
              <div style={{ marginTop: '15px' }}>
                {isEditing ? (
                  <Form fluid>
                    <Form.Group>
                      <Form.ControlLabel>Base Model ID <span style={{ color: 'red' }}>*</span></Form.ControlLabel>
                      <Input
                        value={editFormValue?.base_model_id || ''}
                        onChange={(value) => updateEditField('base_model_id', value)}
                        placeholder="e.g., meta-llama/Llama-2-7b-hf"
                      />
                      <Form.HelpText>Base model ID and actual model name to load in the container</Form.HelpText>
                    </Form.Group>

                    <Form.Group>
                      <Form.ControlLabel>Description</Form.ControlLabel>
                      <Input
                        as="textarea"
                        value={editFormValue?.description || ''}
                        onChange={(value) => updateEditField('description', value)}
                        rows={3}
                        maxLength={2048}
                        placeholder="Enter model description..."
                      />
                      <Form.HelpText>Optional description (up to 2048 characters).</Form.HelpText>
                      <Form.HelpText>{(editFormValue?.description || '').length} / 2048 characters</Form.HelpText>
                    </Form.Group>

                    <Form.Group>
                      <Form.ControlLabel>Instance Type <span style={{ color: 'red' }}>*</span></Form.ControlLabel>
                      <SelectPicker
                        value={editFormValue?.instance_type || null}
                        onChange={(value) => updateEditField('instance_type', value || '')}
                        data={buildInstanceTypeOptions(droplets, editFormValue?.instance_type)}
                        placeholder="Select instance type"
                        searchable
                      />
                      <Form.HelpText>GPU droplet type (e.g., gpu-6000adax1-48gb)</Form.HelpText>
                    </Form.Group>

                    <Form.Group>
                      <Form.ControlLabel>Adapter</Form.ControlLabel>
                      <SelectPicker
                        value={editFormValue?.adapter || null}
                        onChange={(value) => updateEditField('adapter', value || '')}
                        data={savedModels.map(m => ({ 
                          label: m.model_id, 
                          value: m.model_id,
                          description: m.description || '',
                          tags: m.tags || []
                        }))}
                        placeholder="Select adapter from saved models"
                        searchable
                        cleanable
                        renderMenuItem={(label, item) => (
                          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <tbody>
                                <tr>
                                  <td style={{ fontWeight: 500, paddingRight: '12px', verticalAlign: 'top', width: '120px' }}>
                                    Model ID:
                                  </td>
                                  <td style={{ fontWeight: 500, verticalAlign: 'top' }}>
                                    {label}
                                  </td>
                                </tr>
                                {item.description && (
                                  <tr>
                                    <td style={{ paddingRight: '12px', verticalAlign: 'top', color: '#666', fontSize: '0.875em' }}>
                                      Description:
                                    </td>
                                    <td style={{ color: '#666', fontSize: '0.875em', verticalAlign: 'top' }}>
                                      {item.description}
                                    </td>
                                  </tr>
                                )}
                                {item.tags && item.tags.length > 0 && (
                                  <tr>
                                    <td style={{ paddingRight: '12px', verticalAlign: 'top', color: '#666', fontSize: '0.875em' }}>
                                      Tags:
                                    </td>
                                    <td style={{ verticalAlign: 'top', fontSize: '0.875em' }}>
                                      <TagGroup>
                                        {item.tags.map((tag, idx) => (
                                          <Tag key={idx}>{tag}</Tag>
                                        ))}
                                      </TagGroup>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      />
                      <Form.HelpText>Select a saved model to use as LoRA adapter (optional, leave empty to remove)</Form.HelpText>
                    </Form.Group>

                    <Form.Group>
                      <Form.ControlLabel>Idle Timeout (seconds)</Form.ControlLabel>
                      <Input
                        value={editFormValue?.idle_timeout ?? 1800}
                        onChange={(value) => updateEditField('idle_timeout', value)}
                        type="number"
                        min={0}
                        placeholder="1800"
                      />
                      <Form.HelpText>Time in seconds before idle droplet is destroyed (default: 1800 = 30 minutes). Set to 0 to disable automatic cleanup.</Form.HelpText>
                    </Form.Group>

                    <Form.Group>
                      <Form.ControlLabel>Provider Options (JSON)</Form.ControlLabel>
                      <Input
                        as="textarea"
                        value={editFormValue?.provider_options || ''}
                        onChange={(value) => updateEditField('provider_options', value)}
                        rows={3}
                        placeholder='e.g. {"disk_gb": 40}'
                      />
                      <Form.HelpText>Optional provider-specific options as a JSON object. Provider is determined by the instance type.</Form.HelpText>
                    </Form.Group>
                  </Form>
                ) : (
                  <Table data={(() => {
                    const detailsArray = Object.keys(modelDetails)
                      .filter(key => {
                        if (key === 'error' || key === 'provider_options') return false;
                        const value = modelDetails[key];
                        // Filter out null, undefined, and empty arrays
                        if (value === null || value === undefined) return false;
                        if (Array.isArray(value) && value.length === 0) return false;
                        if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
                        return true;
                      })
                      .map(key => ({
                        property: key,
                        value: modelDetails[key]
                      }));
                    
                    return detailsArray;
                  })()} autoHeight>
                    <Column width={200}>
                      <HeaderCell>Property</HeaderCell>
                      <Cell dataKey="property" />
                    </Column>
                    <Column flexGrow={1}>
                      <HeaderCell>Value</HeaderCell>
                      <Cell>
                        {(rowData) => {
                          const value = rowData.value;
                          // Handle objects
                          if (typeof value === 'object' && !Array.isArray(value)) {
                            return (
                              <pre style={{ 
                                margin: 0, 
                                whiteSpace: 'pre-wrap',
                                background: '#2a2a2a',
                                color: '#fff',
                                padding: '10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                wordBreak: 'break-word'
                              }}>
                                {JSON.stringify(value, null, 2)}
                              </pre>
                            );
                          }
                          // Handle arrays
                          if (Array.isArray(value)) {
                            return value.join(', ');
                          }
                          // Handle date fields
                          if (rowData.property === 'created_at' || rowData.property === 'last_used') {
                            return formatDateTime(value);
                          }
                          return String(value);
                        }}
                      </Cell>
                    </Column>
                  </Table>
                )}
              </div>
            </Tabs.Tab>
            <Tabs.Tab eventKey="provider_options" title="Provider Options">
              <div style={{ marginTop: '15px' }}>
                {modelDetails.provider_options && Object.keys(modelDetails.provider_options).length > 0 ? (
                  <pre style={{
                    background: '#2a2a2a',
                    color: '#fff',
                    padding: '15px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0,
                  }}>
                    {JSON.stringify(modelDetails.provider_options, null, 2)}
                  </pre>
                ) : (
                  <div style={{ color: '#aaa', fontStyle: 'italic', padding: '10px 0' }}>
                    No provider options were specified for this model.
                    <br /><br />
                    <strong>Examples:</strong>
                    <pre style={{
                      background: '#2a2a2a',
                      color: '#9cdcfe',
                      padding: '12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginTop: '10px',
                    }}>
{`# Vast.ai — pin to a specific host
{ "host_ids": [55838] }

# Vast.ai — pin to a specific physical machine
{ "machine_id": 12345 }

# Vast.ai — use known offer IDs (one chosen at random; bypasses marketplace search)
{ "offer_ids": [98765432] }

# Vast.ai — explore: prefer hosts outside the reliable-host registry
{ "host_ids": [] }

# DigitalOcean — assign to a specific VPC
{ "vpc_uuid": "c33931f2-a26a-4d9b-b53e-03fa4e51a836" }`}
                    </pre>
                  </div>
                )}
              </div>
            </Tabs.Tab>
            <Tabs.Tab eventKey="json" title="JSON">
              <div style={{ marginTop: '15px' }}>
                <pre style={{ 
                  background: '#2a2a2a', 
                  color: '#fff',
                  padding: '15px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  maxHeight: '500px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0
                }}>
                  {JSON.stringify(modelDetails, null, 2)}
                </pre>
              </div>
            </Tabs.Tab>
          </Tabs>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        {isEditing ? (
          <>
            <Button 
              onClick={() => {
                setLocalEditing(false);
                if (onCancelEdit) {
                  onCancelEdit();
                }
              }} 
              appearance="subtle"
            >
              Cancel
            </Button>
            <Button 
              onClick={onSave}
              appearance="primary"
              loading={savingDetails}
            >
              Save
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={() => {
                setActiveTab('table');
                setLocalEditing(true);
                if (onStartEdit) {
                  onStartEdit();
                }
              }} 
              appearance="primary"
            >
              Edit
            </Button>
            <Button 
              onClick={() => {
                onClose();
              }} 
              appearance="subtle"
            >
              Close
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default InferenceDetailsDialog;

